import React, {useEffect, useReducer, useRef, useState} from 'react';
import {
  AccessibilityInfo,
  Animated,
  Easing,
  Pressable,
  ScrollView,
  useWindowDimensions,
  View,
} from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';
import ChessboardComponent from 'chessboard-package';

// DEVIATION from the plan: relative imports instead of the '@' alias — see
// CourseVideoPlayer.tsx for why. This file is also transitively imported by
// Task 6's index.test.tsx.
import CCText from '../../../Atoms/CCText';
import {CoursePage} from '../../../Hooks/LmsCourseApi/types';

import {
  initialPuzzleRun,
  legalDestinations,
  playFreeMove,
  positionsAlongLine,
  puzzleBoardView,
  puzzleCopy,
  puzzleRunReducer,
  ScoreCell,
  scoresheet,
} from '../courseLogic';
import {formatTimestamp, squareOffset} from '../courseFormat';
import {CARD_SHADOW, contentWidth, F, MOBILE_BREAKPOINT, STATUS} from '../courseTheme';
import {
  bestArrow,
  CourseEnginePanel,
  CourseEvalBar,
  ENGINE_ARROW_COLOR,
  useCourseEngine,
} from '../engine';

import CourseButton, {TurnBadge} from './CourseButton';

/** Horizontal padding CourseChapterScreen puts around the chapter content. */
const PAGE_PADDING = 24;

type Props = {
  page: CoursePage;
  onSolved: () => void;
  width?: number;
  /** Jump the player back to where this position came up in the recording. */
  onReplayMoment?: (seconds: number) => void;
  /** The course this puzzle belongs to, so the tutor can be asked about it. */
  bookUuid?: string;
  /**
   * The coach's current demonstration step, if it is playing one. While this
   * is set the board shows the step's position and ignores input.
   */
  coachStep?: {
    fen: string;
    highlight: string[];
    san: string | null;
    arrows?: string[][];
  } | null;
  /**
   * Hand a hint to the coach instead of showing it here, so every explanation
   * on this screen comes from one place. `square` is set on the second press,
   * the one that points at the board.
   */
  onHint?: (hint: {text: string; square: string | null; level: number}) => void;
  /**
   * Go back to the set-up video, shortly before the coach finished asking.
   * Replaces the "From mm:ss" chip on a posed puzzle: that jumped to where the
   * board first appeared, which in a pose-anchored course is the middle of
   * the set-up, not the question.
   */
  onReplaySetup?: () => void;
  /** Play the coach's answer — offered once the puzzle is solved or shown. */
  onWatchAnswer?: () => void;
};

/** How long the opponent appears to think before replying. */
const REPLY_DELAY_MS = 700;

/**
 * How long a wrong move stays on the board, beside the right one, before the
 * right one is played for the student. Long enough to read "The move was
 * Rxh7+." and see the arrow; short enough not to feel like a penalty box.
 */
const AUTO_ADVANCE_MS = 1800;

const NOT_THE_MOVE = 'Not the move';
const GOOD_MOVE = 'Good move — keep going.';

/**
 * Feedback colours. F.wrongFg on F.wrongBg measures 2.7:1 and F.teal on white
 * 2.8:1 — both fills, not text colours. These pairs clear 4.5:1.
 */
const WRONG = {fg: STATUS.bad.fg, bg: '#FEF3F2'};
const GOOD = {fg: F.tealDark, bg: '#EEF7F6'};
/** The right move, drawn as an arrow while a wrong one is on the board. */
const CORRECT_ARROW = STATUS.good.fg;

/** Width of the eval bar and its gap, taken from the board while analysing. */
const EVAL_BAR_WIDTH = 14;
const EVAL_BAR_GAP = 8;

/**
 * A `single_variant` puzzle: the student plays the odd plies, the scripted
 * engine reply lands automatically, and the final odd ply completes the page.
 *
 * The board is `chessboard-package`, the same one the LMS chapter player uses,
 * so a generated course plays exactly like a hand-authored one. That package
 * makes the move itself and then reports it through `handleNewFen` — there is
 * no snapback and no way to veto a drop. A move is therefore taken back by
 * remounting the board, keyed on `run.board`, which is the same trick the LMS
 * player uses.
 *
 * A wrong move stays on the board, marked, with the right move drawn beside it,
 * until the student chooses: play the right move, try again, or start over.
 * Every transition lives in `puzzleRunReducer`; this component draws it.
 */
export default function CoursePuzzleBoard({
  page,
  onSolved,
  width = 520,
  onReplayMoment,
  bookUuid,
  coachStep = null,
  onHint,
  onReplaySetup,
  onWatchAnswer,
}: Props) {
  const {width: windowWidth} = useWindowDimensions();

  const [run, dispatch] = useReducer(puzzleRunReducer, undefined, () =>
    initialPuzzleRun(page.content, page.moves, page.config?.alternatives),
  );
  const {fen} = run;
  const solved = run.phase === 'solved';
  // While the opponent's reply is pending the board is the opponent's, not the
  // student's — accepting a move then would let them play both sides.
  const thinking = run.phase === 'replying';
  // The page is reported solved once: replaying it after a Restart is practice,
  // not new progress.
  const reportedSolved = useRef(false);
  // 0 = not asked, 1 = the written hint, 2 = the written hint plus the square.
  const [hintLevel, setHintLevel] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  // A tutor answer being shown on the board. The puzzle is paused while this
  // is set, and untouched by it — stepping through an explanation must not
  // count as playing moves.
  const [preview, setPreview] = useState<{
    positions: string[];
    index: number;
    arrows: [string, string][];
    highlight: string[];
    lineSan: string[];
  } | null>(null);

  // Analysis after the solve: free play from the solved position, with the
  // engine following the board. null when not analysing.
  const [analysis, setAnalysis] = useState<{fen: string; lastMoveFen: string} | null>(null);
  // Bumped on leaving analysis: the package kept the free-play moves in its
  // own copy of the position, so the board is remounted to drop them.
  const [analysisRun, setAnalysisRun] = useState(0);
  // The engine is the answer key. It exists only once this run is solved —
  // there is no path to it before that, whatever state `analysis` is in.
  const analysing = !!analysis && solved;
  const engine = useCourseEngine({
    fen: analysis?.fen ?? run.fen,
    enabled: analysing,
    lines: 3,
    depth: 18,
  });

  // A different puzzle in the same component starts from nothing. Skipped on
  // mount — the reducer was just initialised from this page — because loading
  // remounts the board.
  const loadedPage = useRef(`${page.id}:${page.content}`);
  useEffect(() => {
    const key = `${page.id}:${page.content}`;
    if (loadedPage.current === key) return;
    loadedPage.current = key;
    dispatch({
      type: 'load',
      start: page.content,
      moves: page.moves,
      alternatives: page.config?.alternatives,
    });
    reportedSolved.current = false;
    setHintLevel(0);
    setSelected(null);
    setPreview(null);
    setAnalysis(null);
  }, [page.id, page.content, page.moves, page.config?.alternatives]);

  // Show the student's move on its own first, then land the reply a beat
  // later. Applying both at once reads as the opponent moving at the same
  // instant, and the student never sees what they just played. Leaving the
  // phase (Undo, Restart, unmount) cancels it, so a reply can never land on a
  // position it was not scripted for.
  useEffect(() => {
    if (run.phase !== 'replying') return;
    const timer = setTimeout(() => dispatch({type: 'replyLanded'}), REPLY_DELAY_MS);
    return () => clearTimeout(timer);
  }, [run.phase, run.reply]);

  // A mistake is shown, then answered: the wrong move goes, the right one and
  // its reply are played, and the student carries on with the line. The
  // reducer's paused flow (play correct / try again) is driven from here on a
  // timer. Leaving the phase — Undo, Restart, unmount — cancels it. With no
  // readable answer the wrong move is simply taken back.
  useEffect(() => {
    if (run.phase !== 'wrong') return;
    const timer = setTimeout(
      () => dispatch({type: run.correct ? 'playCorrect' : 'tryAgain'}),
      AUTO_ADVANCE_MS,
    );
    return () => clearTimeout(timer);
  }, [run.phase, run.wrong, run.correct]);

  // Reduced motion: the pause above stays (it is reading time, not
  // animation), but pieces jump rather than glide.
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    if (run.solvedOnce && !reportedSolved.current) {
      reportedSolved.current = true;
      onSolved();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run.solvedOnce]);

  const orientation = (page.content.split(' ')[1] ?? 'w') === 'w' ? 'white' : 'black';

  const handleNewFen = (moveData: {from: string; to: string}) => {
    if (!moveData) return;
    const {from, to} = moveData;
    // Picking a piece up and putting it back is not an attempt at the puzzle;
    // treating it as a wrong move scolds the student for doing nothing.
    if (!from || !to || from === to) return;
    // Analysing: any legal move, either side, and the puzzle never hears of it.
    if (analysing && analysis) {
      const next = playFreeMove(analysis.fen, from, to);
      if (next) setAnalysis({fen: next, lastMoveFen: analysis.fen});
      return;
    }
    if (run.phase !== 'idle') return;
    // Judged from the live position on the line, not the starting one — the
    // reducer threads it through.
    dispatch({type: 'move', from, to});
  };

  const config = page.config || {};
  const hintText = (config.hint || '').trim();
  // Courses generated before hints existed carry no hint_square, but the square
  // is just the origin of the scripted first move — which is already here. So
  // the "show me where" hint works on every course, old or new.
  const moment = config.source_timestamp ?? page.timestamp;
  const practice = config.role === 'practice';
  // A posed puzzle's set-up is replayed from its question, not its timestamp.
  const replaySetup = !practice && config.role === 'puzzle' ? onReplaySetup : undefined;
  // Heading, task line and — only once solved — the idea, its type and the
  // explanation. Before that nothing on the card may name the answer.
  const copy = puzzleCopy(page, run.solvedOnce);
  const hasIdea = !!(copy.typeLabel || copy.answerTitle || copy.summary);
  const wrong = run.phase === 'wrong' ? run.wrong : null;
  // Nothing to take back at the start, and nothing to touch while the board is
  // showing someone else's moves.
  const busy = !!coachStep || !!preview;
  // Undo also takes back a wrong move while it is on show.
  const canUndo = !busy && (run.turnStarts.length > 0 || run.phase === 'wrong');
  const canRestart = !busy && (run.phase !== 'idle' || run.turnStarts.length > 0);
  // A hint is only worth offering if there is something to reveal — and not
  // while a wrong move is on show, when the right one already is.
  // The written hint comes first, once; after it (or straight away, when
  // there is none) "Show move" draws the move in hand — on every turn.
  const offerHint = !!hintText && hintLevel === 0;
  const hasLine = (page.moves ?? []).length > 0;
  const canHint = run.phase === 'idle' || thinking;
  const canShowMove = run.phase === 'idle' && !run.revealed && !busy;
  const sheet = scoresheet(run);

  // The position under the student's hand: free play while analysing.
  const boardFen = analysing && analysis ? analysis.fen : fen;
  const destinations = selected ? legalDestinations(boardFen, selected) : [];

  /**
   * Click-to-move, owned here rather than left to chessboard-package.
   *
   * The package has its own click handling, but it keeps the selection in
   * props its wrapper is meant to supply, and in this app that state never
   * updates — clicking a piece highlighted nothing, so the board read as dead
   * even though the move itself landed. This prop is spread onto the inner
   * board last, so it replaces the package's handler. Dragging is untouched.
   */
  const handleSquareClick = (square: string) => {
    if (run.phase !== 'idle' && !analysing) return;

    if (selected && square !== selected) {
      if (destinations.includes(square)) {
        setSelected(null);
        handleNewFen({from: selected, to: square});
        return;
      }
      // Clicking another of your own pieces picks that one up instead of
      // reading as a failed move.
      if (legalDestinations(boardFen, square).length) {
        setSelected(square);
        return;
      }
      setSelected(null);
      return;
    }

    // Clicking the selected piece again puts it back down.
    if (selected === square) {
      setSelected(null);
      return;
    }
    setSelected(legalDestinations(boardFen, square).length ? square : null);
  };

  const choose = (type: 'undo' | 'restart' | 'showMove') => {
    setSelected(null);
    // Undo while a mistake is on show takes that move back (and with it the
    // automatic answer); the reducer's own undo steps over correct moves.
    dispatch({type: type === 'undo' && run.phase === 'wrong' ? 'tryAgain' : type});
  };
  const view = puzzleBoardView(run);

  const canAnalyze = solved && !busy;
  const leaveAnalysis = () => {
    setSelected(null);
    setAnalysis(null);
    setAnalysisRun(n => n + 1);
  };
  const toggleAnalysis = () => {
    if (analysis) {
      leaveAnalysis();
      return;
    }
    setSelected(null);
    setAnalysis({fen: view.position, lastMoveFen: view.lastMoveFen});
  };
  const engineArrow = analysing ? bestArrow(engine.state) : null;

  const available = contentWidth(windowWidth);
  const stacked = available < MOBILE_BREAKPOINT;
  const boardSize =
    (stacked ? Math.min(available - PAGE_PADDING * 2, 520) : Math.min(width, available * 0.42)) -
    // The eval bar sits beside the board, so the board gives up its width
    // rather than pushing the row wider than the page.
    (analysing ? EVAL_BAR_WIDTH + EVAL_BAR_GAP : 0);

  const shownFrom =
    run.phase === 'idle' && run.revealed && !busy && !analysing
      ? squareOffset(run.revealed.from, boardSize, orientation)
      : null;
  const selectedMarker =
    selected && !preview ? squareOffset(selected, boardSize, orientation) : null;
  // Squares the tutor pointed at, drawn the same way the hint marker is — the
  // package's own square styling must not be replaced (see the hint marker).
  const tutorMarkers = (preview?.highlight ?? [])
    .map(sq => ({sq, at: squareOffset(sq, boardSize, orientation)}))
    .filter(m => m.at);

  // The coach is demonstrating: the board shows its step rather than the
  // student's position, and their moves are ignored until it stops. Letting
  // them play into a demonstration would leave the two out of step with no
  // way back to either.
  const coachMarkers = (coachStep?.highlight ?? [])
    .map(sq => ({sq, at: squareOffset(sq, boardSize, orientation)}))
    .filter(m => m.at);

  // The position shown by the coach's *previous* step. The package derives its
  // last-move highlight by diffing the two, so without this each demonstrated
  // move lands with no indication of what just moved.
  const coachPrevRef = useRef<string | null>(null);
  const coachPrevFen = coachPrevRef.current;
  useEffect(() => {
    coachPrevRef.current = coachStep ? coachStep.fen : null;
  }, [coachStep]);

  const showAnswer = (answer: {
    line: string[];
    arrows: [string, string][];
    highlight: string[];
    line_san: string[];
  } | null) => {
    if (!answer) {
      setPreview(null);
      return;
    }
    // Replayed from the position the student is actually looking at. The
    // server validated the line, so this should not truncate — but if it does,
    // the student sees a short line rather than a wrong board.
    const positions = positionsAlongLine(fen, answer.line);
    if (!positions.length) return;
    setSelected(null);
    setPreview({
      positions,
      index: 0,
      arrows: answer.arrows ?? [],
      highlight: answer.highlight ?? [],
      lineSan: answer.line_san ?? [],
    });
  };
  const destinationMarkers = destinations
    .map(sq => ({sq, at: squareOffset(sq, boardSize, orientation)}))
    .filter(d => d.at);
  const wrongMarker = wrong && !busy ? squareOffset(wrong.to, boardSize, orientation) : null;
  const wrongBadge = wrongMarker ? Math.max(16, Math.round(wrongMarker.size * 0.38)) : 0;

  return (
    <View
      style={{
        flexDirection: stacked ? 'column' : 'row',
        gap: stacked ? 16 : 24,
        alignItems: stacked ? 'center' : 'stretch',
        // On a phone the card below the board gives way to the window, not the
        // board: it shrinks and scrolls inside itself (see CardBody).
        ...(stacked ? {flexShrink: 1, minHeight: 0} : null),
      }}>
      <View style={{flexDirection: 'row', alignItems: 'center', gap: EVAL_BAR_GAP}}>
        {analysing ? (
          <CourseEvalBar
            score={engine.state.lines[0]?.score}
            height={boardSize}
            width={EVAL_BAR_WIDTH}
            flipped={orientation === 'black'}
          />
        ) : null}
        <View style={{width: boardSize, borderRadius: 4, overflow: 'hidden'}}>
          {/* The hint marker is an overlay rather than a `customSquareStyles`
              prop: that prop is spread last onto the inner board, so it
              replaces the package's own square styling — including the
              selection highlight, which leaves clicking a piece looking like
              nothing happened. */}
          {/* Selection and legal-move feedback, drawn here for the same reason
              as the hint marker. */}
          {tutorMarkers.map(({sq, at}) => (
            <View
              key={`tutor-${sq}`}
              pointerEvents="none"
              style={{
                position: 'absolute',
                zIndex: 2,
                left: at!.left,
                top: at!.top,
                width: at!.size,
                height: at!.size,
                borderWidth: 3,
                borderColor: F.teal,
                borderRadius: 4,
              }}
            />
          ))}
          {/* What the coach is pointing at as it speaks. Orange rather than
              the tutor's teal, so it is obvious which assistant is talking. */}
          {coachMarkers.map(({sq, at}) => (
            <View
              key={`coach-${sq}`}
              pointerEvents="none"
              style={{
                position: 'absolute',
                zIndex: 3,
                left: at!.left,
                top: at!.top,
                width: at!.size,
                height: at!.size,
                borderWidth: 3,
                borderColor: F.hintFg,
                backgroundColor: F.hintBg,
                borderRadius: 4,
              }}
            />
          ))}
          {/* While the coach demonstrates, the student's moves are swallowed
              here rather than disabled on the package — a board that stops
              responding mid-sentence reads as broken, and letting them play
              into a demonstration desyncs the two with no way back. */}
          {coachStep ? (
            <View
              style={{
                position: 'absolute',
                zIndex: 4,
                left: 0,
                top: 0,
                width: boardSize,
                height: boardSize,
              }}
            />
          ) : null}

          {selectedMarker ? (
            <View
              pointerEvents="none"
              style={{
                position: 'absolute',
                zIndex: 2,
                left: selectedMarker.left,
                top: selectedMarker.top,
                width: selectedMarker.size,
                height: selectedMarker.size,
                backgroundColor: 'rgba(61,171,158,0.45)',
              }}
            />
          ) : null}

          {destinationMarkers.map(({sq, at}) => (
            <View
              key={`dest-${sq}`}
              pointerEvents="none"
              style={{
                position: 'absolute',
                zIndex: 2,
                left: at!.left,
                top: at!.top,
                width: at!.size,
                height: at!.size,
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <View
                style={{
                  width: at!.size * 0.3,
                  height: at!.size * 0.3,
                  borderRadius: at!.size * 0.15,
                  backgroundColor: 'rgba(61,171,158,0.7)',
                }}
              />
            </View>
          ))}

          {/* "Show move": the piece to move, outlined in the arrow's green. */}
          {shownFrom ? (
            <View
              pointerEvents="none"
              style={{
                position: 'absolute',
                zIndex: 2,
                left: shownFrom.left,
                top: shownFrom.top,
                width: shownFrom.size,
                height: shownFrom.size,
                borderWidth: 3,
                borderColor: CORRECT_ARROW,
                borderRadius: 4,
              }}
            />
          ) : null}

          {/* The wrong move's square: a red outline and a cross, so it reads
              as wrong without relying on the colour alone. */}
          {wrongMarker ? (
            <View
              pointerEvents="none"
              style={{
                position: 'absolute',
                zIndex: 3,
                left: wrongMarker.left,
                top: wrongMarker.top,
                width: wrongMarker.size,
                height: wrongMarker.size,
                borderWidth: 3,
                borderColor: WRONG.fg,
                borderRadius: 4,
              }}>
              <View
                style={{
                  position: 'absolute',
                  top: 2,
                  right: 2,
                  width: wrongBadge,
                  height: wrongBadge,
                  borderRadius: wrongBadge / 2,
                  backgroundColor: WRONG.fg,
                  borderWidth: 2,
                  borderColor: F.white,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                <Ionicons name="close" size={Math.round(wrongBadge * 0.7)} color={F.white} />
              </View>
            </View>
          ) : null}
          <ChessboardComponent
            key={`course-board-${page.id}-run${run.board}-a${analysisRun}`}
            position={
              coachStep
                ? coachStep.fen
                : preview
                ? preview.positions[preview.index]
                : analysing && analysis
                ? analysis.fen
                : view.position
            }
            // The package diffs these two to work out what just moved and
            // paints the from/to squares — that is the last-move highlight.
            lastMoveFen={
              coachStep
                ? coachPrevFen ?? coachStep.fen
                : preview
                ? preview.positions[Math.max(0, preview.index - 1)]
                : analysing && analysis
                ? analysis.lastMoveFen
                : view.lastMoveFen
            }
            // react-chessboard takes [from, to, colour]; the package spreads
            // these props last, so the colour here wins over its own yellow.
            customArrows={
              coachStep
                ? (coachStep as any).arrows
                : preview
                ? preview.arrows
                : analysing
                ? engineArrow
                  ? [[engineArrow[0], engineArrow[1], ENGINE_ARROW_COLOR]]
                  : undefined
                : view.arrow
                ? [[view.arrow[0], view.arrow[1], CORRECT_ARROW]]
                : undefined
            }
            boardOrientation={orientation}
            boardWidth={boardSize}
            {...(reducedMotion ? {animationDuration: 0} : null)}
            // Only the student's own turn takes input: not while the reply is
            // coming, a wrong move is waiting on a decision, or it is solved.
            disabled={analysing ? busy : run.phase !== 'idle' || busy}
            handleNewFen={handleNewFen}
            onSquareClick={handleSquareClick}
          />
        </View>
      </View>

      <View
        style={[
          {
            flex: stacked ? undefined : 1,
            width: stacked ? boardSize : undefined,
            ...(stacked ? {flexShrink: 1, minHeight: 0} : null),
            // Beside the board the card is exactly as tall as the board, and its
            // text scrolls inside it. Left to grow, a revealed explanation made
            // the row taller than the board and pushed the chapter's Continue
            // below a container that does not scroll. The board is a sibling,
            // never inside this scroller, so dragging pieces is unaffected.
            height: stacked ? undefined : boardSize,
            backgroundColor: F.white,
            borderRadius: F.radius.card,
            overflow: 'hidden',
          },
          CARD_SHADOW,
        ]}>
        {/* The card's text scrolls inside the card, never around the board:
            beside the board the card is held to the board's height; under it
            on a phone it shrinks to what the window has left. */}
        <ScrollView
          style={stacked ? {flexGrow: 0, flexShrink: 1, minHeight: 0} : {flex: 1, minHeight: 0}}
          contentContainerStyle={
            stacked ? {padding: 16, gap: 16} : {padding: 32, paddingBottom: 16, gap: 16}
          }>
          <View style={{gap: 10}}>
            {/* The moment in the recording is metadata, not a title. It used
                to be the title, which is why every puzzle was called
                "Puzzle at 27:50". */}
            {replaySetup ? (
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="See the set-up again"
                onPress={replaySetup}
                style={chipStyle(false)}>
                <Ionicons name="play-back-circle" size={16} color={F.brown} />
                <CCText
                  style={{fontSize: 13, fontWeight: '600', color: F.brown, letterSpacing: 0.2}}>
                  See the set-up again
                </CCText>
              </Pressable>
            ) : moment != null ? (
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Replay this moment"
                disabled={!onReplayMoment}
                onPress={() => onReplayMoment?.(moment)}
                style={chipStyle(!onReplayMoment)}>
                <Ionicons name="play-circle" size={16} color={F.brown} />
                <CCText
                  style={{
                    fontSize: 13,
                    fontWeight: '600',
                    color: F.brown,
                    letterSpacing: 0.2,
                  }}>
                  {`From ${formatTimestamp(moment)}`}
                </CCText>
              </Pressable>
            ) : null}

            {/* Which side you are playing is a precondition, not a footnote.
                It used to sit under the move list and hint card, which on a
                stacked phone put it below the fold entirely — students picked
                up the wrong colour and got "Not the move" with no idea why. */}
            <TurnBadge turn={orientation === 'white' ? 'w' : 'b'} />

            {/* The type of puzzle ("Mating pattern · Greco's mate") used to sit
                here from the start. It names the answer, so it now arrives
                with the idea once the puzzle is solved. */}
            {practice ? (
              <View
                style={{
                  alignSelf: 'flex-start',
                  backgroundColor: 'rgba(61,171,158,0.12)',
                  borderRadius: 6,
                  paddingHorizontal: 8,
                  paddingVertical: 4,
                }}>
                <CCText style={{fontSize: 12, fontWeight: '600', color: F.tealDark}}>
                  Practice what you just saw
                </CCText>
              </View>
            ) : null}

            <CCText
              style={{
                // The LMS player drops the puzzle title to 18 when stacked;
                // desktop type on a 360px phone wraps a generated title to
                // four lines.
                fontSize: stacked ? 18 : 24,
                fontWeight: '600',
                color: F.brownDark,
                lineHeight: stacked ? 25 : 32,
              }}>
              {copy.heading}
            </CCText>

            {/* The task — "White to play and mate in 2." A legacy page's text
                is its solution, so `puzzleCopy` holds that back until solved. */}
            {copy.line ? (
              <CCText
                style={{
                  fontSize: 16,
                  fontWeight: '600',
                  color: F.brown,
                  lineHeight: 24,
                }}>
                {copy.line}
              </CCText>
            ) : null}
          </View>

          {/* The engine, inside the card's own scroller — never around the
              board, whose drag a scroller would steal. */}
          {analysing ? (
            <View style={{gap: 8}}>
              <CCText style={{fontSize: 13, fontWeight: '500', color: F.mutedText, lineHeight: 19}}>
                Free play: move any piece, for either side. The engine follows the board.
              </CCText>
              <CourseEnginePanel
                enabled
                onToggle={on => {
                  if (!on) leaveAnalysis();
                }}
                state={engine.state}
                status={engine.status}
                maxPlies={6}
              />
            </View>
          ) : null}

          {/* The scoresheet: only the moves already played — listing the
              whole scripted line would hand over the solution. A ply that went
              wrong keeps the mistake beside the move that was right. */}
          {sheet.length ? (
            <View style={{gap: 6}}>
              <CCText
                style={{fontSize: 12, fontWeight: '700', color: F.mutedText, letterSpacing: 0.6}}>
                MOVES
              </CCText>
              {/* Capped like a paper scoresheet: on a wide card, flex columns
                  pushed Black's move ~260px away from White's. */}
              <View style={{gap: 2, maxWidth: 380}}>
                {sheet.map(row => (
                  <View
                    key={row.number}
                    style={{flexDirection: 'row', alignItems: 'flex-start', gap: 6}}>
                    <CCText
                      style={{
                        width: 30,
                        paddingVertical: 4,
                        textAlign: 'right',
                        fontSize: 13,
                        fontWeight: '600',
                        color: F.mutedText,
                        fontVariant: ['tabular-nums'],
                      }}>
                      {row.white ? `${row.number}.` : `${row.number}…`}
                    </CCText>
                    <ScoreSheetCell cell={row.white} side="White" number={row.number} />
                    <ScoreSheetCell cell={row.black} side="Black" number={row.number} />
                  </View>
                ))}
              </View>
              {thinking ? (
                <CCText style={{fontSize: 13, fontWeight: '600', color: F.mutedText}}>
                  Opponent is thinking…
                </CCText>
              ) : null}
            </View>
          ) : null}

          {solved ? (
            <FadeIn>
              <View
                accessible
                accessibilityLiveRegion="polite"
                accessibilityLabel={run.helped ? 'Solved with help' : "Nice! It's correct"}
                style={{flexDirection: 'row', alignItems: 'center', gap: 12, paddingTop: 4}}>
                <View
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 22,
                    backgroundColor: F.teal,
                    borderBottomWidth: 3,
                    borderBottomColor: F.tealDark,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                  <Ionicons name="checkmark" size={26} color={F.white} />
                </View>
                {/* tealDark, not teal: teal text on white is 2.8:1. */}
                <CCText
                  style={{fontSize: 22, fontWeight: '600', color: F.tealDark, flexShrink: 1}}>
                  {/* Not "correct" once a wrong move was made on the way: the
                      right one was shown to them. */}
                  {run.helped ? 'Solved with help' : "Nice! It's correct"}
                </CCText>
              </View>
            </FadeIn>
          ) : null}

          {/* The idea, once it has been found — and it stays found: replaying
              the puzzle after a Restart does not hide it again. */}
          {run.solvedOnce && hasIdea ? (
            <FadeIn delay={solved ? 80 : 0}>
              <View
                style={{
                  gap: 8,
                  backgroundColor: F.badgeBg,
                  borderRadius: 12,
                  borderLeftWidth: 4,
                  borderLeftColor: F.yellow,
                  paddingHorizontal: 16,
                  paddingVertical: 14,
                }}>
                <View
                  style={{flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 8}}>
                  <Ionicons name="bulb-outline" size={16} color={F.brown} />
                  <CCText
                    style={{
                      fontSize: 12,
                      fontWeight: '700',
                      color: F.brown,
                      letterSpacing: 0.6,
                      textTransform: 'uppercase',
                    }}>
                    The idea
                  </CCText>
                  {/* What the puzzle teaches, from the analysis the server held
                      to the board — never a guess made here. */}
                  {copy.typeLabel ? (
                    <View
                      style={{
                        backgroundColor: F.white,
                        borderRadius: 6,
                        borderWidth: 1,
                        borderColor: F.border10,
                        paddingHorizontal: 8,
                        paddingVertical: 3,
                      }}>
                      <CCText style={{fontSize: 12, fontWeight: '600', color: F.brown}}>
                        {copy.typeLabel}
                      </CCText>
                    </View>
                  ) : null}
                </View>
                {copy.answerTitle ? (
                  <CCText
                    style={{
                      fontSize: stacked ? 17 : 18,
                      fontWeight: '600',
                      color: F.brownDark,
                      lineHeight: 24,
                    }}>
                    {copy.answerTitle}
                  </CCText>
                ) : null}
                {copy.summary ? (
                  <CCText
                    style={{fontSize: 15, fontWeight: '500', color: F.brown, lineHeight: 23}}>
                    {copy.summary}
                  </CCText>
                ) : null}
              </View>
            </FadeIn>
          ) : null}

          {run.solvedOnce && onWatchAnswer ? (
            <View style={{alignSelf: 'flex-start'}}>
              <CourseButton
                label="Watch the coach's answer"
                showArrow
                emphasised
                onPress={onWatchAnswer}
              />
            </View>
          ) : null}

          {/* While an explanation is on the board, the controls are for the
              explanation — the puzzle is paused underneath it. */}
          {preview ? (
            <View
              style={{
                backgroundColor: 'rgba(61,171,158,0.10)',
                borderWidth: 1,
                borderColor: F.teal,
                borderRadius: 8,
                padding: 12,
                gap: 10,
              }}>
              <CCText style={{fontSize: 13, fontWeight: '600', color: F.tealDark}}>
                {preview.lineSan.length
                  ? `${preview.lineSan.slice(0, preview.index).join(' ') || 'Start'}  ·  move ${preview.index} of ${preview.positions.length - 1}`
                  : 'Shown on the board'}
              </CCText>
              <View style={{flexDirection: 'row', gap: 8, flexWrap: 'wrap'}}>
                <CourseButton
                  label="Back"
                  variant="review"
                  disabled={preview.index === 0}
                  onPress={() =>
                    setPreview(p => (p ? {...p, index: Math.max(0, p.index - 1)} : p))
                  }
                />
                <CourseButton
                  label="Next move"
                  disabled={preview.index >= preview.positions.length - 1}
                  onPress={() =>
                    setPreview(p =>
                      p
                        ? {...p, index: Math.min(p.positions.length - 1, p.index + 1)}
                        : p,
                    )
                  }
                />
                <CourseButton
                  label="Back to my puzzle"
                  variant="review"
                  onPress={() => setPreview(null)}
                />
              </View>
            </View>
          ) : null}

          {/* "Ask about this position" used to sit here. The course coach in
              the rail does everything it did and more — it knows the position,
              and it can demonstrate a line on the board while talking through
              it — so two assistants on one screen was a choice the student had
              no way to make well. The preview machinery above stays: the coach
              drives it. */}

          {/* The hint card used to sit here. It now arrives in the coach —
              one place every explanation on this screen comes from, so a
              student never has to work out which panel answered them. The
              square is still outlined on the board, because that is board
              feedback rather than an explanation. */}

        </ScrollView>

        {/* The verdict and the controls sit outside the scroller, so neither
            scrolls out of reach — on a phone least of all. */}
        <View
          style={{
            paddingHorizontal: stacked ? 16 : 32,
            paddingTop: 12,
            paddingBottom: stacked ? 16 : 24,
            borderTopWidth: 1,
            borderTopColor: F.border10,
            gap: 12,
          }}>
          {/* Icon, colour and words: a wrong move is never told by red alone.
              Announced politely — a student on a screen reader hears the
              verdict without it cutting across what is being read. */}
          {wrong ? (
            <View
              accessible
              accessibilityLiveRegion="polite"
              accessibilityLabel={
                run.correct
                  ? `${NOT_THE_MOVE}. The move was ${run.correct.san}. Playing it for you.`
                  : `${NOT_THE_MOVE}. Taking it back.`
              }
              style={{
                gap: 6,
                backgroundColor: WRONG.bg,
                borderWidth: 1,
                borderColor: WRONG.fg,
                borderRadius: 8,
                paddingHorizontal: 12,
                paddingVertical: 10,
              }}>
              <View style={{flexDirection: 'row', alignItems: 'center', gap: 8}}>
                <Ionicons name="close-circle" size={18} color={WRONG.fg} />
                <CCText style={{fontSize: 15, fontWeight: '700', color: WRONG.fg}}>
                  {`${NOT_THE_MOVE}.`}
                </CCText>
              </View>
              {run.correct ? (
                <View style={{flexDirection: 'row', alignItems: 'center', gap: 8}}>
                  {/* Green like the arrow on the board, so the two read as one. */}
                  <Ionicons name="arrow-forward-circle" size={18} color={CORRECT_ARROW} />
                  <CCText
                    style={{fontSize: 15, fontWeight: '600', color: F.brownDark, flexShrink: 1}}>
                    {`The move was ${run.correct.san}.`}
                  </CCText>
                </View>
              ) : null}
              <CCText style={{fontSize: 13, fontWeight: '500', color: F.mutedText}}>
                {run.correct ? 'Playing it for you…' : 'Taking it back…'}
              </CCText>
            </View>
          ) : run.verdict === 'good' ? (
            <View
              accessible
              accessibilityLiveRegion="polite"
              accessibilityLabel={GOOD_MOVE}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 8,
                alignSelf: 'flex-start',
                backgroundColor: GOOD.bg,
                borderWidth: 1,
                borderColor: GOOD.fg,
                borderRadius: 8,
                paddingHorizontal: 12,
                paddingVertical: 8,
              }}>
              <Ionicons name="checkmark-circle" size={18} color={GOOD.fg} />
              <CCText style={{fontSize: 14, fontWeight: '600', color: GOOD.fg, flexShrink: 1}}>
                {GOOD_MOVE}
              </CCText>
            </View>
          ) : null}

          {/* Never more than three controls at once: Hint or Show move (or
              Analyze, once solved), Undo move, Restart — or, while analysing,
              Analyze and Back to puzzle. */}
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
              flexWrap: 'wrap',
            }}>
            {analysing ? (
              <>
                <PuzzleControl
                  toggle
                  checked
                  icon="analytics-outline"
                  label="Analyze"
                  disabled={busy}
                  onPress={toggleAnalysis}
                />
                <PuzzleControl
                  icon="return-up-back"
                  label="Back to puzzle"
                  disabled={busy}
                  onPress={leaveAnalysis}
                />
              </>
            ) : (
              <>
                {/* Solved: analysis is the one thing left to offer. Before
                    that, never — the engine would hand over the answer. */}
                {canAnalyze ? (
                  <PuzzleControl
                    toggle
                    checked={false}
                    icon="analytics-outline"
                    label="Analyze"
                    disabled={false}
                    onPress={toggleAnalysis}
                  />
                ) : solved ? (
                  <View />
                ) : offerHint ? (
                  <PuzzleControl
                    icon="bulb-outline"
                    label="Hint"
                    disabled={!canHint || busy}
                    onPress={() => {
                      setHintLevel(1);
                      // The words go to the coach, which is where every other
                      // explanation on this screen comes from.
                      onHint?.({text: hintText, square: null, level: 1});
                    }}
                  />
                ) : hasLine ? (
                  <PuzzleControl
                    icon="eye-outline"
                    label="Show move"
                    disabled={!canShowMove}
                    onPress={() => choose('showMove')}
                  />
                ) : (
                  <View />
                )}
                {/* Disabled — not hidden — at the start, so the student learns
                    they exist. */}
                <View style={{flexDirection: 'row', gap: 8, flexWrap: 'wrap'}}>
                  <PuzzleControl
                    icon="arrow-undo"
                    label="Undo move"
                    disabled={!canUndo}
                    onPress={() => choose('undo')}
                  />
                  <PuzzleControl
                    icon="refresh"
                    label="Restart"
                    disabled={!canRestart}
                    onPress={() => choose('restart')}
                  />
                </View>
              </>
            )}
          </View>
        </View>
      </View>
      {/* Draws nothing; null unless analysing, so no worker exists before. */}
      {engine.engineElement}
    </View>
  );
}

/** Whether the system asks for reduced motion; follows the setting live. */
function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    let active = true;
    const asked: any = AccessibilityInfo.isReduceMotionEnabled?.();
    if (asked && typeof asked.then === 'function') {
      asked.then((on: boolean) => active && setReduced(!!on)).catch(() => {});
    }
    const sub: any = AccessibilityInfo.addEventListener?.('reduceMotionChanged', (on: boolean) =>
      setReduced(!!on),
    );
    return () => {
      active = false;
      sub?.remove?.();
    };
  }, []);
  return reduced;
}

/** The latest move's cell. 4.6:1+ for every text colour used on it. */
const CURRENT_CELL = '#EEF7F6';

/**
 * One side's move in the scoresheet. A clean move is just its SAN; a ply that
 * went wrong shows each wrong try struck through in red with a cross, then the
 * right move in green with a tick — icon, colour and word, and read out as
 * "Qh5+, wrong; Rxh7+, correct".
 */
function ScoreSheetCell({
  cell,
  side,
  number,
}: {
  cell: ScoreCell | null;
  side: 'White' | 'Black';
  number: number;
}) {
  if (!cell) return <View style={{flex: 1}} />;
  const said = cell.wrong.length
    ? [...cell.wrong.map(w => `${w}, wrong`), ...(cell.san ? [`${cell.san}, correct`] : [])].join(
        '; ',
      )
    : cell.san;
  return (
    <View
      accessible
      accessibilityLabel={`Move ${number}, ${side}: ${said}`}
      accessibilityState={{selected: cell.current}}
      style={{
        flex: 1,
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'center',
        columnGap: 8,
        rowGap: 2,
        paddingHorizontal: 6,
        paddingVertical: 4,
        borderRadius: 6,
        backgroundColor: cell.current ? CURRENT_CELL : 'transparent',
      }}>
      {cell.wrong.map(w => (
        <View key={w} style={{flexDirection: 'row', alignItems: 'center', gap: 2}}>
          <Ionicons name="close" size={14} color={WRONG.fg} />
          <CCText
            style={{
              fontSize: 14,
              fontWeight: '600',
              color: WRONG.fg,
              textDecorationLine: 'line-through',
            }}>
            {w}
          </CCText>
        </View>
      ))}
      {cell.san && cell.wrong.length ? (
        <View style={{flexDirection: 'row', alignItems: 'center', gap: 2}}>
          <Ionicons name="checkmark" size={14} color={CORRECT_ARROW} />
          <CCText style={{fontSize: 14, fontWeight: '700', color: CORRECT_ARROW}}>{cell.san}</CCText>
        </View>
      ) : cell.san ? (
        <CCText
          style={{
            fontSize: 14,
            fontWeight: cell.current ? '700' : '600',
            color: F.brownDark,
          }}>
          {cell.san}
        </CCText>
      ) : null}
    </View>
  );
}

/**
 * The "See the set-up again" / "From 27:50" pills. They were 26px tall — a
 * fingertip target well under 44px — with no pressed or focus state.
 */
const chipStyle =
  (disabled: boolean) =>
  ({pressed, hovered, focused}: any) => ({
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    alignSelf: 'flex-start' as const,
    gap: 6,
    minHeight: 44,
    backgroundColor: pressed && !disabled ? '#EFE5CC' : hovered && !disabled ? '#F3EBD6' : F.badgeBg,
    borderRadius: 999,
    paddingHorizontal: 14,
    opacity: disabled ? 0.45 : 1,
    cursor: disabled ? 'not-allowed' : 'pointer',
    ...(focused && !disabled
      ? {outlineStyle: 'solid', outlineWidth: 2, outlineColor: F.tealDark, outlineOffset: 2}
      : null),
  });

/**
 * A short fade-and-rise for what appears when the puzzle is solved: enough to
 * draw the eye to it, quiet enough not to feel like a slot machine. Skipped
 * when the system asks for reduced motion.
 */
function FadeIn({children, delay = 0}: {children: React.ReactNode; delay?: number}) {
  const progress = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    let active = true;
    const animation = Animated.timing(progress, {
      toValue: 1,
      duration: 240,
      delay,
      easing: Easing.out(Easing.cubic),
      // The native driver does not exist on web.
      useNativeDriver: false,
    });
    animation.start();
    const reduce: any = AccessibilityInfo.isReduceMotionEnabled?.();
    if (reduce && typeof reduce.then === 'function') {
      reduce
        .then((on: boolean) => {
          if (on && active) {
            animation.stop();
            progress.setValue(1);
          }
        })
        .catch(() => {});
    }
    return () => {
      active = false;
      animation.stop();
    };
    // Plays once, when it first appears.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <Animated.View
      style={{
        opacity: progress,
        transform: [
          {translateY: progress.interpolate({inputRange: [0, 1], outputRange: [6, 0]})},
        ],
      }}>
      {children}
    </Animated.View>
  );
}

/**
 * Undo / Restart: an outlined secondary button with an icon and a word, 44px
 * tall, with visible hover, pressed and keyboard-focus states. Subordinate to
 * the chunky hint button, which is the action a stuck student should reach for
 * first.
 */
function PuzzleControl({
  icon,
  label,
  disabled,
  onPress,
  toggle = false,
  checked = false,
}: {
  icon: string;
  label: string;
  disabled: boolean;
  onPress: () => void;
  /** An on/off switch: announced as one, and filled tealDark while on. */
  toggle?: boolean;
  checked?: boolean;
}) {
  const on = toggle && checked;
  return (
    <Pressable
      accessibilityRole={toggle ? 'switch' : 'button'}
      accessibilityLabel={label}
      accessibilityState={toggle ? {disabled, checked} : {disabled}}
      disabled={disabled}
      onPress={onPress}
      // `hovered` and `focused` are react-native-web's; native passes neither.
      style={({pressed, hovered, focused}: any) => ({
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        minHeight: 44,
        minWidth: 44,
        paddingHorizontal: 14,
        borderRadius: F.radius.button,
        borderWidth: 1.5,
        borderColor: on ? F.tealDark : '#806234',
        // On: filled tealDark under white words, 5.2:1.
        backgroundColor: on
          ? pressed && !disabled
            ? '#22645C'
            : F.tealDark
          : disabled
          ? F.white
          : pressed
          ? '#EFE5CC'
          : hovered
          ? F.badgeBg
          : F.white,
        // Same disabled treatment as CourseButton.
        opacity: disabled ? 0.45 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        ...(focused && !disabled
          ? {outlineStyle: 'solid', outlineWidth: 2, outlineColor: F.tealDark, outlineOffset: 2}
          : null),
      })}>
      <Ionicons name={icon} size={18} color={on ? F.white : F.brown} />
      <CCText
        style={{fontSize: 14, fontWeight: '600', color: on ? F.white : F.brown}}>
        {label}
      </CCText>
    </Pressable>
  );
}
